import { useState } from 'react';
import { toast } from 'sonner';
import { eventUpdateRequestsAPI } from '../lib/api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

/**
 * Shared dialog for submitting a correction / update request on any event.
 *
 * Props:
 *   open, onOpenChange   — controlled modal state
 *   eventId              — required
 *   eventTitle           — optional, shown in the dialog header for context
 *   defaultFieldHint     — optional, pre-fills the "what field is wrong" input
 *   mailtoFallbackHref   — optional mailto URL shown if the request POST fails
 *                          (network outage, logged out, etc.)
 */
export const UpdateRequestDialog = ({
  open,
  onOpenChange,
  eventId,
  eventTitle,
  defaultFieldHint = '',
  mailtoFallbackHref,
}) => {
  const [fieldHint, setFieldHint] = useState(defaultFieldHint);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const reset = () => {
    setMessage('');
    setFieldHint(defaultFieldHint);
    setSubmitted(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 5) {
      toast.error('Please describe the change you\'d like — a few words is enough.');
      return;
    }
    setSubmitting(true);
    try {
      await eventUpdateRequestsAPI.submit(eventId, {
        message: message.trim(),
        field_hint: fieldHint.trim() || null,
      });
      setSubmitted(true);
      toast.success('Update request sent to the admin team');
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
        'Could not send your request. Please try the email link instead.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(reset, 150);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
      <DialogContent className="max-w-lg" data-testid="update-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            Request an update
          </DialogTitle>
          <DialogDescription>
            Send a suggestion to the AMSCompass admin team.
            {eventTitle && <> This is for <strong className="text-[#1E3A5F]">{eventTitle}</strong>.</>}
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-6 text-center space-y-3" data-testid="update-request-success">
            <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto" />
            <p className="font-semibold text-[#1E3A5F]">Thanks — your request is in the queue.</p>
            <p className="text-sm text-[#4A4A4A]">
              An admin will review it soon. You can submit more if you spot anything else.
            </p>
            <Button onClick={close} className="btn-primary mt-2">Close</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="req-field" className="text-sm font-medium text-[#1E3A5F]">
                Which field needs a change? <span className="text-[#71717A]">(optional)</span>
              </Label>
              <Input
                id="req-field"
                value={fieldHint}
                onChange={(e) => setFieldHint(e.target.value)}
                placeholder="e.g. Date, Booth cost, Deadline, Address…"
                className="input-default"
                maxLength={80}
                data-testid="update-request-field"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="req-msg" className="text-sm font-medium text-[#1E3A5F]">
                What&rsquo;s the correct info?
              </Label>
              <Textarea
                id="req-msg"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us exactly what to change and, if possible, a source link."
                className="input-default min-h-[120px]"
                maxLength={4000}
                data-testid="update-request-message"
                required
              />
              <p className="text-xs text-[#71717A]">{message.length}/4000</p>
            </div>
            <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
              {mailtoFallbackHref && (
                <a
                  href={mailtoFallbackHref}
                  className="inline-flex items-center justify-center text-xs text-[#71717A] underline hover:text-[#1E3A5F] mr-auto"
                >
                  Prefer email?
                </a>
              )}
              <Button type="button" variant="outline" onClick={close}>Cancel</Button>
              <Button
                type="submit"
                className="btn-primary"
                disabled={submitting}
                data-testid="update-request-submit"
              >
                {submitting
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
                  : 'Send to admins'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UpdateRequestDialog;
